// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — every colour that carries meaning, in one place so tests/contrast.test.ts can hold it
// to WCAG 2.1 §1.4.11 (≥3:1 for a non-text graphic) against every surface it can actually sit on.
//
// SIX TERRAIN FILLS IS THE HARD CASE, and the rule that makes it tractable is that they are all
// BRIGHT and everything drawn ON them is INK. Every fill sits in relative luminance 0.20–0.50, the
// demesne behind them at 0.0105 and an empty cell at 0.0168, so the worst fill still clears 4.9:1
// against an empty cell; the coronet pips and terrain glyphs sit at 0.0057, so the worst pip clears
// 5.9:1 against the darkest fill. Nothing is ever ink-on-dark or fill-on-fill.
//
// COLOUR IS NEVER THE ONLY CHANNEL. Six hues cannot survive deuteranopia on their own — pine, loch
// and reed are a green, a blue and a violet, and two of those converge. Every terrain therefore
// carries a GLYPH (see GLYPH below), and the contrast test says so in words: delete the glyphs and
// this palette becomes a lie.
//
// The empty cell is deliberately a *lighter* square than the demesne behind it, so the 5×5 grid is
// legible as a grid before a single doublet is placed — on this board "where can this go" is the
// whole decision, and a frame you cannot see is a decision you cannot make.

export const PALETTE = {
  night: '#12161C',
  panel: '#1B2119',
  panelHi: '#252D22',

  /** The demesne board behind the cells, and the ring around it. */
  demesne: '#161C14',
  rim: '#0E1319',
  /** An empty, placeable cell. */
  empty: '#1E251C',
  /** The line between cells. Structure, and on this board structure is meaning. */
  grid: '#6E7A5E',

  /** The six terrains. Ordered by scarcity — Wold is where area comes from, Coomb the multiplier. */
  wold: '#C8B93E',
  pine: '#2FA37B',
  loch: '#56B4E9',
  reed: '#8E7BE8',
  orch: '#E8834A',
  coomb: '#9AA2B0',

  /** The centre cell. Wild for adjacency, in no region, worth nothing. */
  motte: '#F2D98C',

  /** Everything drawn on top of a terrain fill: the glyph and the coronet pips. */
  ink: '#141014',

  /** A cell the lifted doublet may legally be anchored on, at the current rotation. */
  legal: '#FFFFFF',
  /** The ghost of the doublet under your finger. */
  ghost: '#FFE08A',
  /** An empty region that can never be filled (parity), and an illegal drop. */
  spoiled: '#C0553F',

  text: '#E9F0E4',
  textMuted: '#A8B4A0',
  gold: '#F2B441',
  alarm: '#FF9B76',
} as const;

/** The six terrain keys, in scarcity order. Index IS the terrain id used by rules.ts. */
export const TERRAIN_KEYS = ['wold', 'pine', 'loch', 'reed', 'orch', 'coomb'] as const;
export type TerrainKey = (typeof TERRAIN_KEYS)[number];

export const TERRAIN_NAME: Record<TerrainKey, string> = {
  wold: 'Wold',
  pine: 'Pinewood',
  loch: 'Loch',
  reed: 'Reedbed',
  orch: 'Orchard',
  coomb: 'Coomb',
};

/**
 * The second channel. Each is an SVG path drawn in `ink` inside a 0..24 box, so the terrain survives
 * greyscale, protanopia, deuteranopia and a bad phone screen. These are not decoration and the
 * contrast test asserts one exists for every terrain.
 */
export const GLYPH: Record<TerrainKey, string> = {
  // a tuft of grass
  wold: 'M6 18c0-4 1-6 3-8M12 18c0-6 0-9 0-12M18 18c0-4-1-6-3-8',
  // a conifer
  pine: 'M12 4l5 8h-3l3 6H7l3-6H7z',
  // a wave
  loch: 'M4 10c2-2 4-2 6 0s4 2 6 0 2-1 4-1M4 16c2-2 4-2 6 0s4 2 6 0 2-1 4-1',
  // reeds — upright bars of unequal height
  reed: 'M7 19V8M12 19V5M17 19V10',
  // a fruit on a bough
  orch: 'M12 8a5 5 0 100 10 5 5 0 000-10M12 8V4M12 5c2-1 3-1 4-2',
  // scree chevrons
  coomb: 'M4 15l8-7 8 7M4 20l8-7 8 7',
};

/** Player colours. Each demesne is private, so these label a name and a strip, never a cell. */
export const SEAT_COLOUR = ['#F2B441', '#7FC2FF', '#C99BFF', '#6BE0B0'];
export const SEAT_NAME = ['Amber', 'Sky', 'Violet', 'Mint'];

/** Every surface a meaningful mark is actually DRAWN ON. */
export const SURFACES = [
  'demesne',
  'empty',
  'panel',
  'wold',
  'pine',
  'loch',
  'reed',
  'orch',
  'coomb',
  'motte',
] as const;

/**
 * Which surfaces each mark can ACTUALLY sit on. A blanket cross-product would be a lie in both
 * directions — an ink glyph is invisible on an empty cell and never appears there; a legal-anchor
 * outline only ever rings an EMPTY cell. Working this map out honestly is most of the value.
 */
export const MARK_SURFACES: Record<string, ReadonlyArray<string>> = {
  wold: ['demesne', 'empty'],
  pine: ['demesne', 'empty'],
  loch: ['demesne', 'empty'],
  reed: ['demesne', 'empty'],
  orch: ['demesne', 'empty'],
  coomb: ['demesne', 'empty'],
  motte: ['demesne', 'empty'],
  grid: ['demesne', 'empty'],
  // the ink channel — glyphs and coronet pips, only ever on a terrain fill
  ink: ['wold', 'pine', 'loch', 'reed', 'orch', 'coomb', 'motte'],
  // interaction marks — only ever on an empty cell or the demesne behind it
  legal: ['demesne', 'empty'],
  ghost: ['demesne', 'empty'],
  spoiled: ['demesne', 'empty'],
  // seat colours label the rival strip, which sits on the panel
  seat: ['panel'],
};

/** Everything that has to be findable, in the order the contrast test reports them. */
export const ON_BOARD = [
  'wold',
  'pine',
  'loch',
  'reed',
  'orch',
  'coomb',
  'motte',
  'grid',
  'ink',
  'legal',
  'ghost',
  'spoiled',
] as const;
